import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { supabase } from "./supabaseClient";

function fmtDate(d) {
  try {
    return new Date(d).toLocaleString();
  } catch {
    return "—";
  }
}

function planLabel(planCode) {
  if (!planCode) return "Free";
  if (planCode.startsWith("starter")) return "Starter";
  if (planCode.startsWith("studio")) return "Studio";
  if (planCode === "free") return "Free";
  return planCode;
}

export default function Settings() {
  const navigate = useNavigate();
  const { currentUser, signOut: signOutFromAuth } = useAuth();

  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  // Billing state
  const [subLoading, setSubLoading] = useState(true);
  const [subError, setSubError] = useState("");
  const [subscription, setSubscription] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  const allowanceMinutes = useMemo(() => {
    const hours = subscription?.subscription_plans?.hours_per_month;
    if (typeof hours !== "number") return null;
    return hours * 60;
  }, [subscription]);

  // Load subscription row for this user
  useEffect(() => {
    let cancelled = false;

    (async () => {
      setSubLoading(true);
      setSubError("");
      try {
        const { data: userRes, error: userErr } = await supabase.auth.getUser();
        const user = userRes?.user;
        if (userErr || !user) {
          if (!cancelled) {
            setSubscription(null);
            setSubLoading(false);
          }
          return;
        }

        const { data, error } = await supabase
          .from("subscriptions")
          .select(
            `
            user_id,
            plan_code,
            stripe_status,
            cancel_at_period_end,
            cancel_at,
            current_period_start,
            current_period_end,
            stripe_customer_id,
            stripe_subscription_id,
            subscription_plans ( hours_per_month, max_games )
          `
          )
          .eq("user_id", user.id)
          .maybeSingle();

        if (error) throw error;

        if (!cancelled) setSubscription(data || null);
      } catch (e) {
        console.error("Failed to load subscription:", e);
        if (!cancelled) setSubError("Failed to load your subscription. Please refresh.");
      } finally {
        if (!cancelled) setSubLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const openBillingPortal = async () => {
    setPortalLoading(true);
    setSubError("");
    try {
      const { data, error } = await supabase.functions.invoke("create-portal-session", {
        body: { returnUrl: `${window.location.origin}/dashboard/settings` },
      });

      if (error) {
        console.error("create-portal-session error:", error);
        const msg = error.message || "Could not open billing portal.";
        setSubError(msg);
        return;
      }

      const url = data?.url;
      if (!url) {
        setSubError("Billing portal did not return a URL.");
        return;
      }

      window.location.href = url;
    } catch (e) {
      console.error(e);
      setSubError("Could not open billing portal. Please try again.");
    } finally {
      setPortalLoading(false);
    }
  };

  // Handle account deletion (soft-delete your profile row)
  const handleDeleteAccount = async () => {
    if (!currentUser || deleting) return;

    setDeleting(true);
    setError("");

    try {
      const userId = currentUser.id || currentUser.uid;
      if (!userId) throw new Error("No user id available on currentUser");

      // Prefer profiles (matches your schema). If your app uses a different table, swap it here.
      const { error: updateError } = await supabase
        .from("profiles")
        .update({
          deleted_at: new Date().toISOString(),
          is_deleted: true,
        })
        .eq("id", userId);

      if (updateError) throw updateError;

      await signOutFromAuth();
      navigate("/");
    } catch (err) {
      console.error("Error deleting account:", err);
      setError("Something went wrong deleting your account. Please try again.");
      setDeleting(false);
    }
  };

  return (
    <div className="ml-0 md:ml-0 flex-1 flex flex-col justify-start bg-[#111] text-[#eee] font-nunito">
      <div className="w-full max-w-3xl space-y-6 md:space-y-8">
        <h2 className="text-xl md:text-2xl font-semibold tracking-tight">Settings</h2>

        {/* Billing / subscription */}
        <div className="w-full md:w-[800px] bg-[#151515] border border-[#252525] shadow-md rounded-xl p-6 space-y-4">
          <div className="text-lg font-semibold">Plan & billing</div>

          {subLoading ? (
            <div className="text-sm text-[#777]">Loading your plan…</div>
          ) : subError ? (
            <div className="text-sm text-red-400 bg-red-900/30 border border-red-700 rounded px-3 py-2">
              {subError}
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div className="rounded-lg border border-[#252525] bg-black/20 p-3">
                  <div className="text-sm text-[#aaa]">Plan</div>
                  <div className="text-base font-semibold">{planLabel(subscription?.plan_code)}</div>
                  <div className="text-sm text-[#aaa] mt-1">
                    Subscription status:{" "}
                    <span className="text-[#eee]">
                      {subscription?.stripe_status || (subscription?.plan_code ? "active" : "free tier")}
                    </span>
                    {subscription?.cancel_at_period_end ? (
                      <span className="ml-2 text-yellow-300">(cancels at period end)</span>
                    ) : null}
                  </div>
                </div>

                <div className="rounded-lg border border-[#252525] bg-black/20 p-3">
                  <div className="text-sm text-[#aaa]">Allowance</div>
                  <div className="text-base font-semibold">
                    {allowanceMinutes != null ? `${allowanceMinutes} minutes / month` : "—"}
                  </div>
                  <div className="text-sm text-[#aaa] mt-1">
                    {subscription?.cancel_at_period_end ? "Ends:" : "Renews:"}{" "}
                    <span className="text-[#eee]">
                      {subscription?.current_period_end ? fmtDate(subscription.current_period_end) : "—"}
                    </span>
                  </div>
                </div>
              </div>

              <div className="flex flex-col md:flex-row gap-2 pt-2">
                <button
                  onClick={openBillingPortal}
                  disabled={portalLoading}
                  className="px-4 py-2 rounded-md bg-yellow-500 text-black font-semibold text-sm disabled:opacity-60"
                >
                  {portalLoading ? "Opening billing…" : "Manage subscription"}
                </button>

                {/* <Link
                  to="/purchase"
                  className="px-4 py-2 rounded-md border border-[#444] text-[#ddd] hover:border-[#38bdf8] hover:text-[#38bdf8] text-sm text-center"
                >
                  Change plan
                </Link> */}
              </div>

              <p className="text-sm text-[#aaa]">
                “Manage subscription” opens Stripe’s secure customer portal where you can update your payment method or cancel your subscription.
              </p>
            </>
          )}
        </div>

      </div>
    </div>
  );
}
